<template>
  <div :key="messageId" class="w-message" role="alert" :class="componentClasses">
    <w-tooltip :text="text ? text : undefined" :color="color" :disabled="variant != 'only-icon'">
      <div class="w-message__content">
        <w-chip
          v-if="icon && displayIcon"
          class="w-message__icon"
          align="center"
          justify="center"
          text-align="center"
          :tabindex="variant === 'only-icon' ? 0 : undefined"
          :variant="chipVariant"
          :icon="icon"
          :icon-config="iconConfig"
          :icon-size="iconSize"
          :size="chipSize"
          :color="color"
        />

        <div v-if="displayText" class="w-message__text">
          <slot>
            <div v-if="text" v-html="translate(text)" />
          </slot>
        </div>
      </div>
    </w-tooltip>
  </div>
</template>

<script lang="ts">
//@ts-ignore
import { defineComponent, computed, PropType, useSlots } from 'vue';

import { defaults } from '../../composables/defaults';
import { withColorProps, useColorProps } from '../../composables/props/colors';
import { withIconProps } from '../../composables/props/icon';
import { withMessageVariantProps } from '../../composables/props/messageVariant';
import { useTranslation } from '../../composables/translation';
import type { SizeType } from '../../typings/sizeType';
import WChip from '../w-chip/WChip.vue';
import WTooltip from '../w-tooltip/WTooltip.vue';

/* Alert umieszczany pod różnymi rodzajami inputów */
export default defineComponent({
  components: { WChip, WTooltip },
  props: {
    ...withColorProps(defaults.message.color),
    ...withIconProps(defaults.message.icon.icon, defaults.message.icon.size, defaults.message.icon.config),
    ...withMessageVariantProps,

    /* Treść komunikatu */
    text: { type: String as PropType<string | null> },

    /* Identyfikator komunikatu */
    messageId: { type: String },

    /* Czy płynnie rozciągać interfejs po pojawieniu się komunikatu */
    dynamic: { type: Boolean, default: defaults.message.dynamic },

    /* Wielkość chipsa z ikoną */
    chipSize: { type: String as PropType<SizeType>, default: defaults.message.chipSize },

    /* Czy pokazywać ikonę*/
    displayIcon: {
      type: Boolean,
      default: true
    }
  },

  setup(props) {
    const componentName = 'w-message';

    const slots = useSlots();

    /** Wyliczone klasy modala */

    const colorProps = useColorProps(componentName, props);

    const chipVariant = computed(() => {
      if (props.variant == 'only-icon') {
        return defaults.message.icon.tooltipChipVariant;
      }
      return defaults.message.icon.defaultChipVariant;
    });

    const componentClasses = computed(() => {
      return [
        colorProps.value,
        `${componentName}--${props.variant}`,
        props.dynamic ? `${componentName}--dynamic` : `${componentName}--static`
      ];
    });

    const displayText = computed(() => {
      return (props.text || slots.default) && props.variant != 'only-icon';
    });

    return { ...useTranslation(), componentClasses, displayText, defaults, chipVariant };
  }
});
</script>
<style lang="scss">
@use 'WMessage';
</style>
